import apiClient from './axios';
import { clearStoredTokens } from '@/store/authStore';
import type { FlotaListKpis, DepartmentListKpis, TenderStatus, PushSubscriptionInfo, EmailOutboxItem, EmailOutboxQuery, EmailOutboxStatus } from '@/types';

// Shared pagination query type
export interface PaginationQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  [key: string]: any;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function buildParams(query?: PaginationQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const str = params.toString();
  return str ? `?${str}` : '';
}

function getFileNameFromDisposition(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const plainMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return plainMatch?.[1] ?? null;
}

// Auth API
export const authApi = {
  register: async (data: { email: string; password: string; name: string }) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await apiClient.post('/auth/login', data);
    return response.data;
  },

  googleLogin: async (idToken: string) => {
    const response = await apiClient.post('/auth/google', { idToken });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      password,
    });
    return response.data;
  },

  logout: async () => {
    clearStoredTokens();
  },

  getProfile: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  impersonate: async (userId: string) => {
    const response = await apiClient.post(`/auth/impersonate/${userId}`);
    return response.data;
  },
};


export const usersApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/users${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  /** Reenvía la invitación de acceso (enlace para establecer contraseña). */
  sendInvite: async (id: string) => {
    const response = await apiClient.post(`/users/${id}/send-invite`);
    return response.data;
  },

  create: async (data: {
    email: string;
    password: string;
    name: string;
    roleIds?: string[];
    isActive?: boolean;
    departmentIds?: string[];
    clientIds?: string[];
    projectOrderIds?: string[];
  }) => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },

  update: async (
    id: string,
    data: {
      name?: string;
      email?: string;
      roleIds?: string[];
      password?: string;
      isActive?: boolean;
      departmentIds?: string[];
      clientIds?: string[];
      projectOrderIds?: string[];
    },
  ) => {
    const response = await apiClient.patch(`/users/${id}`, data);
    return response.data;
  },

  /** Baja lógica (restaurable). */
  delete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  /** Borrado físico; solo tras la baja lógica. */
  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}/permanent`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/users/${id}/restore`);
    return response.data;
  },

  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.patch('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.patch('/users/me/password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// Clients API
export const clientsApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/clients${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/clients/${id}`);
    return response.data;
  },

  create: async (data: { name: string; website?: string | null; isActive?: boolean; createInHarvest?: boolean }) => {
    const response = await apiClient.post('/clients', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; website?: string | null; isActive?: boolean; harvestClientId?: number | null }) => {
    const response = await apiClient.patch(`/clients/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/clients/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/clients/${id}/restore`);
    return response.data;
  },

  exportExcel: async () => {
    const response = await apiClient.get('/clients/export', { responseType: 'blob' });
    const fileName = getFileNameFromDisposition(response.headers['content-disposition']) ?? 'clientes_export.xlsx';
    return { blob: response.data as Blob, fileName };
  },

  importExcel: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/clients/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data as { imported: number; updated: number; skipped: number; errors: string[] };
  },
};

// Departments API
export const departmentsApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/departments${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/departments/${id}`);
    return response.data;
  },

  create: async (data: { name: string; isActive?: boolean }) => {
    const response = await apiClient.post('/departments', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; isActive?: boolean }) => {
    const response = await apiClient.patch(`/departments/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/departments/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/departments/${id}/restore`);
    return response.data;
  },
};

// Order Types API
export const orderTypesApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/order-types${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/order-types/${id}`);
    return response.data;
  },

  create: async (data: { name: string; isActive?: boolean }) => {
    const response = await apiClient.post('/order-types', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; isActive?: boolean }) => {
    const response = await apiClient.patch(`/order-types/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/order-types/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/order-types/${id}/restore`);
    return response.data;
  },
};

// Suppliers API
export const suppliersApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/suppliers${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/suppliers/${id}`);
    return response.data;
  },

  create: async (data: { name: string; cif?: string; email?: string; phone?: string; address?: string }) => {
    const response = await apiClient.post('/suppliers', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; cif?: string; email?: string; phone?: string; address?: string; isActive?: boolean }) => {
    const response = await apiClient.patch(`/suppliers/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/suppliers/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/suppliers/${id}/restore`);
    return response.data;
  },

  exportExcel: async () => {
    const response = await apiClient.get('/suppliers/export', { responseType: 'blob' });
    const fileName = getFileNameFromDisposition(response.headers['content-disposition']) ?? 'proveedores_export.xlsx';
    return { blob: response.data as Blob, fileName };
  },

  importExcel: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/suppliers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data as { imported: number; updated: number; skipped: number; errors: string[] };
  },
};

// Project Orders API
export const projectOrdersApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/project-orders${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/project-orders/${id}`);
    return response.data;
  },

  create: async (data: {
    clientId: string;
    departmentId: string;
    orderTypeId: string;
    projectName: string;
    description?: string;
    createInHarvest?: boolean;
    amount?: number;
    budgetIsMonthly?: boolean;
    tenderStatus?: TenderStatus;
  }) => {
    const response = await apiClient.post('/project-orders', data);
    return response.data;
  },

  archive: async (id: string) => {
    const response = await apiClient.post(`/project-orders/${id}/archive`);
    return response.data;
  },

  update: async (id: string, data: {
    clientId?: string;
    departmentId?: string;
    orderTypeId?: string;
    projectName?: string;
    description?: string;
    isActive?: boolean;
    tenderStatus?: TenderStatus;
    harvestProjectId?: number | null;
    amount?: number;
    budgetIsMonthly?: boolean;
  }) => {
    const response = await apiClient.patch(`/project-orders/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/project-orders/${id}`);
    return response.data;
  },

  createFamily: async (data: {
    clientId: string;
    departmentId: string;
    orderTypeId: string;
    projectName: string;
    description?: string;
    createInHarvest?: boolean;
    siblings: Array<{
      departmentId: string;
      orderTypeId: string;
      createInHarvest?: boolean;
    }>;
  }) => {
    const response = await apiClient.post('/project-orders/family', data);
    return response.data;
  },

  addFamilyMembers: async (rootId: string, siblings: Array<{
    departmentId: string;
    orderTypeId: string;
    createInHarvest?: boolean;
  }>) => {
    const response = await apiClient.post(`/project-orders/${rootId}/members`, { siblings });
    return response.data;
  },

  unlinkFamilyMember: async (rootId: string, childId: string) => {
    const response = await apiClient.delete(`/project-orders/${rootId}/members/${childId}`);
    return response.data;
  },

  reactivate: async (id: string, reactivateChildren = false) => {
    const response = await apiClient.post(`/project-orders/${id}/reactivate`, { reactivateChildren });
    return response.data;
  },
};

// Harvest API (for admin tooling)
export const harvestApi = {
  getProjects: async (clientId?: number): Promise<{ id: number; name: string; code: string; linkedOrderNumber: string | null }[]> => {
    const params = clientId ? { clientId } : {};
    const response = await apiClient.get('/dashboard/harvest-projects', { params });
    return response.data;
  },

  getProject: async (id: number): Promise<{ id: number; name: string; code: string; isActive: boolean } | null> => {
    const response = await apiClient.get(`/dashboard/harvest-projects/${id}`);
    return response.data;
  },

  getClients: async (): Promise<{ id: number; name: string }[]> => {
    const response = await apiClient.get('/dashboard/harvest-clients');
    return response.data;
  },
};

// Facturación — Ingresos API
export const ingresosApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/facturacion/ingresos${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/facturacion/ingresos/${id}`);
    return response.data;
  },

  create: async (data: Record<string, any>) => {
    const response = await apiClient.post('/facturacion/ingresos', data);
    return response.data;
  },

  update: async (id: string, data: Record<string, any>) => {
    const response = await apiClient.patch(`/facturacion/ingresos/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/facturacion/ingresos/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/facturacion/ingresos/${id}/restore`);
    return response.data;
  },

  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/facturacion/ingresos/${id}/hard`);
    return response.data;
  },

  bulkUpdate: async (ids: string[], data: Record<string, any>): Promise<{ count: number }> => {
    const response = await apiClient.patch('/facturacion/ingresos/bulk-update', { ids, data });
    return response.data;
  },
};

// Facturación — Types API
export const facturacionTypesApi = {
  getPaymentTerms: async () => {
    const response = await apiClient.get('/facturacion/types/payment-terms');
    return response.data;
  },
};

// Facturación — Categories API
export const facturacionCategoriesApi = {
  getAll: async (params?: Record<string, unknown>) => {
    const response = await apiClient.get('/facturacion/categories', { params });
    return response.data;
  },
  getByDepartment: async (code: string, scope?: string) => {
    const response = await apiClient.get(`/facturacion/categories/by-department/${code}`, { params: scope ? { scope } : {} });
    return response.data;
  },
  getTree: async (departmentId?: string) => {
    const response = await apiClient.get('/facturacion/categories/tree', { params: departmentId ? { departmentId } : {} });
    return response.data;
  },
  create: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/facturacion/categories', data);
    return response.data;
  },
  update: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.patch(`/facturacion/categories/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await apiClient.delete(`/facturacion/categories/${id}`);
    return response.data;
  },
};

// Facturación — Summary API
export const facturacionSummaryApi = {
  get: async (): Promise<{
    ingresos: { vencidasCount: number; vencidasTotal: number; pendientesCount: number; pendientesTotal: number; byMonth: Record<string, { vencidas: number; vencidasTotal: number; pendientes: number; pendientesTotal: number }> };
    gastos: { vencidasCount: number; vencidasTotal: number; pendientesCount: number; pendientesTotal: number; byMonth: Record<string, { vencidas: number; vencidasTotal: number; pendientes: number; pendientesTotal: number }> };
  }> => {
    const response = await apiClient.get('/facturacion/summary');
    return response.data;
  },
};

// Facturación — Import API
export const facturacionImportApi = {
  importExcel: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/facturacion/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  exportExcel: async (opts?: { ingresoIds?: string[]; gastoIds?: string[] }) => {
    const params: Record<string, string> = {};
    if (opts?.ingresoIds !== undefined) params.ingresoIds = opts.ingresoIds.join(',');
    if (opts?.gastoIds !== undefined) params.gastoIds = opts.gastoIds.join(',');
    const response = await apiClient.get('/facturacion/export', {
      params,
      responseType: 'blob',
    });
    const fileName =
      getFileNameFromDisposition(response.headers['content-disposition']) ??
      'facturacion_export.xlsx';
    return { blob: response.data as Blob, fileName };
  },
  devReset: async () => {
    const response = await apiClient.delete('/facturacion/dev/reset');
    return response.data as { ingresos: number; gastos: number };
  },
  devSeed: async () => {
    const response = await apiClient.post('/facturacion/dev/seed');
    return response.data as { ok: boolean };
  },
};

// Facturación — Objetivos API
export const facturacionObjetivosApi = {
  /** Aggregated targets per month (for dashboard chart) */
  getByYear: async (year: number): Promise<{ month: number; target: number }[]> => {
    const response = await apiClient.get(`/facturacion/objetivos?year=${year}`);
    return response.data;
  },
  /** Full per-department data (for objetivos page matrix) */
  getAllByYear: async (year: number): Promise<{ id: string; departmentId: string | null; year: number; month: number; target: number; department: { id: string; name: string; code: string } | null }[]> => {
    const response = await apiClient.get(`/facturacion/objetivos/year?year=${year}`);
    return response.data;
  },
  /** Bulk save per-department objetivos for a year */
  saveYear: async (year: number, entries: { departmentId: string | null; month: number; target: number }[]): Promise<void> => {
    await apiClient.put('/facturacion/objetivos/year', { year, entries });
  },
};

// Facturación — Gastos API
export const gastosApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/facturacion/gastos${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/facturacion/gastos/${id}`);
    return response.data;
  },

  create: async (data: Record<string, any>) => {
    const response = await apiClient.post('/facturacion/gastos', data);
    return response.data;
  },

  update: async (id: string, data: Record<string, any>) => {
    const response = await apiClient.patch(`/facturacion/gastos/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/facturacion/gastos/${id}`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/facturacion/gastos/${id}/restore`);
    return response.data;
  },

  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/facturacion/gastos/${id}/hard`);
    return response.data;
  },

  bulkUpdate: async (ids: string[], data: Record<string, any>): Promise<{ count: number }> => {
    const response = await apiClient.patch('/facturacion/gastos/bulk-update', { ids, data });
    return response.data;
  },
};

// HR Expenses API
export const hrExpensesApi = {
  getByYear: async (year: number): Promise<{ id: string; departmentId: string | null; year: number; month: number; amount: number; department: { id: string; name: string; code: string } | null }[]> => {
    const response = await apiClient.get(`/hr-expenses/year?year=${year}`);
    return response.data;
  },

  saveYear: async (year: number, entries: { departmentId: string | null; month: number; amount: number }[]): Promise<void> => {
    await apiClient.put('/hr-expenses/year', { year, entries });
  },
};

// Fleet Brands API
export const fleetBrandsApi = {
  getAll: async (query?: { isActive?: boolean }): Promise<{ id: string; name: string; isActive: boolean; order: number }[]> => {
    const params = query?.isActive !== undefined ? `?isActive=${query.isActive}` : '';
    const response = await apiClient.get(`/fleet-brands${params}`);
    return response.data;
  },

  create: async (data: { name: string; order?: number }): Promise<{ id: string; name: string }> => {
    const response = await apiClient.post('/fleet-brands', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; isActive?: boolean; order?: number }): Promise<{ id: string; name: string }> => {
    const response = await apiClient.patch(`/fleet-brands/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/fleet-brands/${id}`);
  },

  restore: async (id: string): Promise<void> => {
    await apiClient.patch(`/fleet-brands/${id}/restore`);
  },

  getMonthlyCountsByYear: async (year: number): Promise<{ id: string; fleetBrandId: string; year: number; month: number; carCount: number; fleetBrand: { id: string; name: string } }[]> => {
    const response = await apiClient.get(`/fleet-brands/monthly-counts?year=${year}`);
    return response.data;
  },

  saveMonthlyCountsYear: async (year: number, entries: { fleetBrandId: string; month: number; carCount: number }[]): Promise<void> => {
    await apiClient.put('/fleet-brands/monthly-counts', { year, entries });
  },
};

// Admin API (SUPER_ADMIN only — dev operations)
export const adminApi = {
  syncFromProd: async () => {
    const response = await apiClient.post('/admin/sync-from-prod');
    return response.data as { status: string; message: string };
  },
  getSyncProdStatus: async () => {
    const response = await apiClient.get('/admin/sync-prod-status');
    return response.data;
  },
  getSyncProdLogs: async (limit = 50) => {
    const response = await apiClient.get(`/admin/sync-prod-logs?limit=${limit}`);
    return response.data;
  },
};

// Notifications API
export const notificationsApi = {
  getAll: async (query?: { page?: number; limit?: number; unreadOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.limit) params.set('limit', String(query.limit));
    if (query?.unreadOnly) params.set('unreadOnly', 'true');
    const qs = params.toString();
    const response = await apiClient.get(`/notifications${qs ? `?${qs}` : ''}`);
    return response.data;
  },
  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },
  markAsRead: async (id: string) => {
    const response = await apiClient.patch(`/notifications/${id}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await apiClient.patch('/notifications/read-all');
    return response.data;
  },
  delete: async (id: string) => {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data;
  },
  deleteMany: async (ids: string[]) => {
    const response = await apiClient.delete('/notifications/bulk', { data: { ids } });
    return response.data;
  },
  deleteAll: async () => {
    const response = await apiClient.delete('/notifications/all');
    return response.data;
  },
  getPreferences: async () => {
    const response = await apiClient.get('/notifications/preferences');
    return response.data;
  },
  updatePreferences: async (preferences: Array<{ eventType: string; channel: string; enabled: boolean }>) => {
    const response = await apiClient.put('/notifications/preferences', { preferences });
    return response.data;
  },
  getEventTypes: async () => {
    const response = await apiClient.get('/notifications/event-types');
    return response.data;
  },
  getRouting: async () => {
    const response = await apiClient.get('/notifications/routing');
    return response.data;
  },
  saveRouting: async (rules: Array<{ eventType: string; targetType: string; targetId: string }>) => {
    const response = await apiClient.put('/notifications/routing', { rules });
    return response.data;
  },
  // Admin — notification types
  adminListTypes: async (params?: { page?: number; limit?: number; search?: string; label?: string; eventKey?: string; isActive?: boolean; category?: string; severity?: string; isSystem?: boolean; hasRules?: boolean; createdAt?: string; updatedAt?: string; sortBy?: string; sortOrder?: string }) => {
    const response = await apiClient.get('/notifications/admin/types', { params });
    return response.data;
  },
  adminCreateType: async (data: { eventKey: string; label: string; description?: string; severity?: string; category: string; icon?: string; color?: string }) => {
    const response = await apiClient.post('/notifications/admin/types', data);
    return response.data;
  },
  adminUpdateType: async (id: string, data: { label?: string; description?: string; severity?: string; category?: string; icon?: string; color?: string; isActive?: boolean }) => {
    const response = await apiClient.patch(`/notifications/admin/types/${id}`, data);
    return response.data;
  },
  adminDeleteType: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/types/${id}`);
    return response.data;
  },
  // Admin — notification rules
  adminListRules: async (params?: { page?: number; limit?: number; search?: string; name?: string; isActive?: boolean; notificationTypeId?: string; severity?: string; recipientType?: string; titleTemplate?: string; priority?: number; createdAt?: string; updatedAt?: string; sortBy?: string; sortOrder?: string }) => {
    const response = await apiClient.get('/notifications/admin/rules', { params });
    return response.data;
  },
  adminCreateRule: async (data: {
    name: string; notificationTypeId: string; conditions?: unknown;
    titleTemplate: string; bodyTemplate?: string; severity?: string;
    recipientType: string; recipientConfig: Record<string, unknown>;
    excludeActor?: boolean; channels?: string[]; priority?: number;
  }) => {
    const response = await apiClient.post('/notifications/admin/rules', data);
    return response.data;
  },
  adminUpdateRule: async (id: string, data: {
    name?: string; conditions?: unknown; titleTemplate?: string; bodyTemplate?: string;
    severity?: string; recipientType?: string; recipientConfig?: Record<string, unknown>;
    excludeActor?: boolean; channels?: string[]; priority?: number; isActive?: boolean;
  }) => {
    const response = await apiClient.patch(`/notifications/admin/rules/${id}`, data);
    return response.data;
  },
  adminDeleteRule: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/rules/${id}`);
    return response.data;
  },
  adminTestRule: async (id: string, payload: Record<string, unknown>) => {
    const response = await apiClient.post(`/notifications/admin/rules/${id}/test`, { payload });
    return response.data;
  },
  adminGetEventsCatalog: async () => {
    const response = await apiClient.get('/notifications/admin/events');
    return response.data;
  },
  adminSendTest: async (data: { ruleId?: string; title?: string; body?: string; severity?: string; channels?: string[] }) => {
    const response = await apiClient.post('/notifications/admin/send-test', data);
    return response.data;
  },
  // Web push subscriptions (current user)
  getPushPublicKey: async (): Promise<{ publicKey: string }> => {
    const response = await apiClient.get('/notifications/push/public-key');
    return response.data;
  },
  getPushSubscriptions: async (): Promise<PushSubscriptionInfo[]> => {
    const response = await apiClient.get('/notifications/push/subscriptions');
    return response.data;
  },
  subscribePush: async (data: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }): Promise<PushSubscriptionInfo> => {
    const response = await apiClient.post('/notifications/push/subscribe', data);
    return response.data;
  },
  unsubscribePush: async (endpoint: string) => {
    await apiClient.delete('/notifications/push/subscribe', { params: { endpoint } });
  },
  // Email outbox (read-only history, permission `correos`)
  emailOutboxList: async (params?: EmailOutboxQuery): Promise<{ data: EmailOutboxItem[]; meta: { total: number; page: number; limit: number; totalPages: number } }> => {
    const response = await apiClient.get('/email-outbox', { params });
    return response.data;
  },
  emailOutboxStats: async (params?: EmailOutboxQuery): Promise<Record<EmailOutboxStatus, number>> => {
    const response = await apiClient.get('/email-outbox/stats', { params });
    return response.data;
  },
  emailOutboxGet: async (id: string): Promise<EmailOutboxItem> => {
    const response = await apiClient.get(`/email-outbox/${id}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardApi = {
  getBusiness: async (year?: number) => {
    const params = year ? `?year=${year}` : '';
    const response = await apiClient.get(`/dashboard/business${params}`);
    return response.data;
  },

  getBusinessYears: async (): Promise<number[]> => {
    const response = await apiClient.get('/dashboard/business-years');
    return response.data;
  },

  getFlota: async (year?: number) => {
    const params = year ? `?year=${year}` : '';
    const response = await apiClient.get(`/dashboard/flota${params}`);
    return response.data;
  },

  getFlotaListKpis: async (params: { year?: number; months?: number[] } = {}): Promise<FlotaListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/flota/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getEventoListKpis: async (params: { year?: number; months?: number[]; tenderStatus?: TenderStatus } = {}): Promise<DepartmentListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/evento/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getPrListKpis: async (params: { year?: number; months?: number[]; tenderStatus?: TenderStatus } = {}): Promise<DepartmentListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/pr/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getRrssListKpis: async (params: { year?: number; months?: number[]; tenderStatus?: TenderStatus } = {}): Promise<DepartmentListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/rrss/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getComunicacionListKpis: async (params: { year?: number; months?: number[]; tenderStatus?: TenderStatus } = {}): Promise<DepartmentListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/comunicacion/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getMediaHubListKpis: async (params: { year?: number; months?: number[]; tenderStatus?: TenderStatus } = {}): Promise<DepartmentListKpis> => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.months && params.months.length > 0) qs.append('months', params.months.join(','));
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/media-hub/list-kpis${q ? '?' + q : ''}`);
    return response.data;
  },

  getAccountManager: async (year?: number, onlyInactive?: boolean) => {
    const qs = new URLSearchParams();
    if (year) qs.append('year', String(year));
    if (onlyInactive) qs.append('onlyInactive', 'true');
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/account-manager${q ? '?' + q : ''}`);
    return response.data;
  },

  getProjectProfitability: async (params: {
    months?: string;
    year?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
    filters?: Record<string, string>;
    clientId?: string;
    projectOrderId?: string;
    departmentCode?: string;
    includeInactive?: boolean;
    onlyInactive?: boolean;
    tenderStatus?: TenderStatus;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.months) qs.append('months', params.months);
    if (params.year) qs.append('year', String(params.year));
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortOrder) qs.append('sortOrder', params.sortOrder);
    if (params.search) qs.append('search', params.search);
    if (params.clientId) qs.append('clientId', params.clientId);
    if (params.projectOrderId) qs.append('projectOrderId', params.projectOrderId);
    if (params.departmentCode) qs.append('departmentCode', params.departmentCode);
    if (params.includeInactive) qs.append('includeInactive', 'true');
    if (params.onlyInactive) qs.append('onlyInactive', 'true');
    if (params.tenderStatus) qs.append('tenderStatus', params.tenderStatus);
    if (params.filters) {
      for (const [k, v] of Object.entries(params.filters)) {
        if (v) qs.append(k, v);
      }
    }
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/project-profitability${q ? '?' + q : ''}`);
    return response.data;
  },

  getClientProfitability: async (params: {
    year?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', String(params.year));
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortOrder) qs.append('sortOrder', params.sortOrder);
    if (params.search) qs.append('search', params.search);
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/client-profitability${q ? '?' + q : ''}`);
    return response.data;
  },

  getDirectionProfitability: async (params: {
    months?: string;
    year?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
    filters?: Record<string, string>;
    clientId?: string;
    onlyInactive?: boolean;
    includeInactive?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.months) qs.append('months', params.months);
    if (params.year) qs.append('year', String(params.year));
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortOrder) qs.append('sortOrder', params.sortOrder);
    if (params.search) qs.append('search', params.search);
    if (params.clientId) qs.append('clientId', params.clientId);
    if (params.onlyInactive) qs.append('onlyInactive', 'true');
    if (params.includeInactive) qs.append('includeInactive', 'true');
    if (params.filters) {
      for (const [k, v] of Object.entries(params.filters)) {
        if (v) qs.append(k, v);
      }
    }
    const q = qs.toString();
    const response = await apiClient.get(`/dashboard/direction-profitability${q ? '?' + q : ''}`);
    return response.data;
  },

  getDirection: async (month?: number, year?: number) => {
    const params = new URLSearchParams();
    if (month) params.append('month', String(month));
    if (year) params.append('year', String(year));
    const qs = params.toString();
    const response = await apiClient.get(`/dashboard/direction${qs ? '?' + qs : ''}`);
    return response.data;
  },

  getBillingVsTarget: async (year: number, departmentId?: string): Promise<{ month: string; facturacion: number; objetivo: number }[]> => {
    const params = new URLSearchParams({ year: String(year) });
    if (departmentId) params.append('departmentId', departmentId);
    const response = await apiClient.get(`/dashboard/billing-vs-target?${params.toString()}`);
    return response.data;
  },

  getBillingVsTargetDirection: async (year: number, departmentId?: string): Promise<{ data: { month: string; facturacion: number; objetivo: number }[]; availableDepartments: { id: string; name: string }[] }> => {
    const params = new URLSearchParams({ year: String(year) });
    if (departmentId) params.append('departmentId', departmentId);
    const response = await apiClient.get(`/dashboard/billing-vs-target-direction?${params.toString()}`);
    return response.data;
  },

  syncHarvest: async (type: "full" | "incremental" = "full") => {
    const response = await apiClient.post(`/dashboard/sync-harvest?type=${type}`, {}, { timeout: 600000 });
    return response.data;
  },

  getSyncStatus: async () => {
    const response = await apiClient.get('/dashboard/sync-status');
    return response.data;
  },

  getSyncLogs: async (limit = 50) => {
    const response = await apiClient.get(`/dashboard/sync-logs?limit=${limit}`);
    return response.data;
  },

  getClientAnalytics: async (clientId: string, year?: number, months?: number[]) => {
    const params = new URLSearchParams({ clientId });
    if (year) params.append('year', String(year));
    if (months && months.length > 0) params.append('months', months.join(','));
    const response = await apiClient.get(`/dashboard/client-analytics?${params.toString()}`);
    return response.data;
  },
  getTopClientByMargin: async (year?: number): Promise<{ id: string; name: string } | null> => {
    const params = year ? `?year=${year}` : '';
    const response = await apiClient.get(`/dashboard/top-client-by-margin${params}`);
    return response.data;
  },

  getTopEventByMargin: async (year?: number): Promise<{ id: string; orderNumber: string; projectName: string; isActive: boolean; clientId: string; departmentId: string; orderTypeId: string; userId: string; createdAt: string; updatedAt: string } | null> => {
    const params = year ? `?year=${year}` : '';
    const response = await apiClient.get(`/dashboard/top-event-by-margin${params}`);
    return response.data;
  },

  getEventAnalytics: async (projectOrderId: string, year?: number, months?: number[]) => {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (months && months.length > 0) params.append('months', months.join(','));
    const qs = params.toString();
    const response = await apiClient.get(`/dashboard/event-analytics/${projectOrderId}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  getFleetAnalytics: async (projectOrderId: string, year?: number, months?: number[]) => {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (months && months.length > 0) params.append('months', months.join(','));
    const qs = params.toString();
    const response = await apiClient.get(`/dashboard/fleet-analytics/${projectOrderId}${qs ? '?' + qs : ''}`);
    return response.data;
  },
};

// ─── Roles & Permissions API ──────────────────────────────────────────────────

export interface PermissionFlags {
  canView: boolean;
  /** Visibilidad en el menú lateral, independiente de `canView`. */
  canViewSidebar: boolean;
  canCreate: boolean;
  canEdit: boolean;
  /** Editar campos sensibles (protegidos) de un registro. */
  canEditCore: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canIssue: boolean;
  canExecute: boolean;
}

export interface FeatureMeta {
  key: string;
  label: string;
  section?: string;
  actions: (keyof PermissionFlags)[];
}

/** Cambio que el servidor aplicó en cascada al guardar un permiso (p. ej.
 *  activar `canViewSidebar` también activa `canView`). */
export interface ImpliedPermissionChange {
  feature: string;
  action: keyof PermissionFlags;
  value: boolean;
}

/** Respuesta de `PUT /roles/permissions/:role/:feature`: los flags guardados
 *  más los cambios implícitos aplicados a otras filas/acciones. */
export interface UpdatePermissionResponse extends PermissionFlags {
  implied?: ImpliedPermissionChange[];
}

export type UpdatePermissionPayload = Partial<PermissionFlags>;

export type PermissionsMap = Record<string, PermissionFlags>;

export const rolesApi = {
  /** Returns effective permissions for the current user (union of all their roles) */
  getMyPermissions: async (): Promise<PermissionsMap> => {
    const response = await apiClient.get('/roles/permissions/me');
    return response.data;
  },

  /** Returns all roles with all their permissions — for management UI */
  getAllPermissions: async (): Promise<Record<string, PermissionsMap>> => {
    const response = await apiClient.get('/roles/permissions');
    return response.data;
  },

  /** Returns features with their labels and available actions */
  getFeatures: async (): Promise<FeatureMeta[]> => {
    const response = await apiClient.get('/roles/features');
    return response.data;
  },

  /** Update a specific role+feature permission. Returns the saved flags plus
   *  the `implied` cascade the server applied. */
  updatePermission: async (
    role: string,
    feature: string,
    data: UpdatePermissionPayload,
  ): Promise<UpdatePermissionResponse> => {
    const response = await apiClient.put(`/roles/permissions/${role}/${feature}`, data);
    return response.data;
  },

  /** List roles (built-in + custom) — requires roles.canView. Por defecto
   *  solo los activos; `onlyInactive` lista las bajas. */
  listRoles: async (
    opts: { includeInactive?: boolean; onlyInactive?: boolean } = {},
  ) => {
    const params: Record<string, string> = {};
    if (opts.includeInactive) params.includeInactive = 'true';
    if (opts.onlyInactive) params.onlyInactive = 'true';
    const response = await apiClient.get('/roles', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  /** Restaura un rol dado de baja */
  restoreRole: async (id: string): Promise<void> => {
    await apiClient.patch(`/roles/${id}/restore`);
  },

  /** Borrado físico real de un rol previamente dado de baja */
  hardDeleteRole: async (id: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}/permanent`);
  },

  /** List roles as options for user assignment — requires usuarios.canEdit */
  listRoleOptions: async () => {
    const response = await apiClient.get('/roles/options');
    return response.data;
  },

  /** Create a new custom role */
  createRole: async (data: { name: string; label: string }) => {
    const response = await apiClient.post('/roles', data);
    return response.data;
  },

  /** Update a role's display label */
  updateRole: async (id: string, data: { label: string }) => {
    const response = await apiClient.patch(`/roles/${id}`, data);
    return response.data;
  },

  /** Delete a custom role */
  deleteRole: async (id: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}`);
  },
};

// ── Database Admin ──────────────────────────────────────────────────────

export const maintenanceApi = {
  getLogHistory: async (params: {
    page?: number; limit?: number; level?: string;
    context?: string; search?: string; from?: string; to?: string;
  }): Promise<{
    data: { id: string; ts: string; level: string; context: string; message: string }[];
    total: number; page: number; limit: number; totalPages: number;
  }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.level) qs.set("level", params.level);
    if (params.context) qs.set("context", params.context);
    if (params.search) qs.set("search", params.search);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const response = await apiClient.get(`/maintenance/logs/history?${qs}`);
    return response.data;
  },
};

export const databaseApi = {
  listTables: async (): Promise<{ table_name: string; row_count: string }[]> => {
    const response = await apiClient.get('/database/tables');
    return response.data;
  },
  getColumns: async (table: string): Promise<{ column_name: string; data_type: string; is_nullable: string; column_default: string | null }[]> => {
    const response = await apiClient.get(`/database/tables/columns?table=${table}`);
    return response.data;
  },
  getTableData: async (table: string, page?: number, limit?: number): Promise<{ data: Record<string, unknown>[]; total: number }> => {
    const params = new URLSearchParams({ table });
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));
    const response = await apiClient.get(`/database/tables/data?${params}`);
    return response.data;
  },
  executeQuery: async (sql: string): Promise<{ rows?: Record<string, unknown>[]; rowCount?: number; duration?: number; type?: string; error?: string }> => {
    const response = await apiClient.post('/database/query', { sql });
    return response.data;
  },
};

// ─── Tareas programadas (Sistema) ──────────────────────────────────────────
export const scheduledTasksApi = {
  catalog: async (): Promise<import('@/types').ScheduledTaskCatalogItem[]> => {
    const response = await apiClient.get('/scheduled-tasks/catalog');
    return response.data;
  },
  getAll: async (params?: {
    isActive?: boolean;
    actionType?: string;
    category?: string;
    isSystem?: boolean;
  }): Promise<import('@/types').ScheduledTask[]> => {
    const response = await apiClient.get('/scheduled-tasks', { params });
    return response.data;
  },
  getById: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const response = await apiClient.get(`/scheduled-tasks/${id}`);
    return response.data;
  },
  create: async (data: Partial<import('@/types').ScheduledTask>): Promise<import('@/types').ScheduledTask> => {
    const response = await apiClient.post('/scheduled-tasks', data);
    return response.data;
  },
  update: async (id: string, data: Partial<import('@/types').ScheduledTask>): Promise<import('@/types').ScheduledTask> => {
    const response = await apiClient.patch(`/scheduled-tasks/${id}`, data);
    return response.data;
  },
  activate: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const response = await apiClient.patch(`/scheduled-tasks/${id}/activate`);
    return response.data;
  },
  deactivate: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const response = await apiClient.patch(`/scheduled-tasks/${id}/deactivate`);
    return response.data;
  },
  run: async (id: string): Promise<import('@/types').ScheduledTaskRunResult> => {
    const response = await apiClient.post(`/scheduled-tasks/${id}/run`);
    return response.data;
  },
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scheduled-tasks/${id}`);
  },
  executions: async (
    id: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<import('@/types').ScheduledTaskExecutionsPage> => {
    const response = await apiClient.get(`/scheduled-tasks/${id}/executions`, { params });
    return response.data;
  },
};

// ── Audit (historial de cambios) ─────────────────────────────────────────

import type {
  AuditAction as AuditActionType,
  AuditListParams as AuditListParamsType,
  AuditListResponse as AuditListResponseType,
  AuditTimelineResponse as AuditTimelineResponseType,
} from "@/types";

function auditListQuery(
  params?: AuditListParamsType,
): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (!params) return q;
  if (params.limit) q.limit = params.limit;
  if (params.cursor) q.cursor = params.cursor;
  if (params.actions && params.actions.length > 0) {
    q.actions = params.actions.join(",");
  }
  if (params.entityType) q.entityType = params.entityType;
  if (params.userId) q.userId = params.userId;
  if (params.from) q.from = params.from;
  if (params.to) q.to = params.to;
  if (params.search) q.search = params.search;
  if (params.event) q.event = params.event;
  if (params.origin) q.origin = params.origin;
  return q;
}

export const auditApi = {
  /** Línea temporal de una entidad concreta (`EntityTimeline`). */
  getTimeline: async (
    entityType: string,
    entityId: string,
    params?: { limit?: number; cursor?: string; actions?: AuditActionType[] },
  ): Promise<AuditTimelineResponseType> => {
    const response = await apiClient.get(`/audit/${entityType}/${entityId}`, {
      params: auditListQuery(params),
    });
    return response.data;
  },
  /** Historial de negocio (sin entidades de sistema). Permiso `audit`. */
  list: async (params?: AuditListParamsType): Promise<AuditListResponseType> => {
    const response = await apiClient.get("/audit", {
      params: auditListQuery(params),
    });
    return response.data;
  },
  /** Historial completo del sistema. Permiso `audit-system`. */
  listSystem: async (
    params?: AuditListParamsType,
  ): Promise<AuditListResponseType> => {
    const response = await apiClient.get("/audit/system", {
      params: auditListQuery(params),
    });
    return response.data;
  },
  /** Personas con actividad en el historial de sistema (filtro "Usuario"). */
  getSystemActors: async (): Promise<
    Array<{ id: string; name: string; email: string }>
  > => {
    const response = await apiClient.get("/audit/system/actors");
    return response.data;
  },
  getEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get("/audit/entity-types");
    return response.data;
  },
  getSystemEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get("/audit/system/entity-types");
    return response.data;
  },
};
